/**
 * useFileUpload Hook
 *
 * Owns the file queue of the document dialog. In create mode the queue holds
 * any number of files (multi-select, drag and drop); in edit mode it is
 * capped to one file, which is exposed through `selectedFile` for the
 * replace-file section.
 *
 * All validation and queue bookkeeping lives in ./uploadQueue (pure
 * functions); this hook only wires them to React state. `itemsRef` mirrors
 * the state synchronously so the upload runner in useDocumentDialog never
 * reads a stale closure while awaiting network calls.
 */

import { useState, useRef, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
  UploadItem,
  patchItem,
  validateFiles,
} from './uploadQueue';

export interface UseFileUploadOptions {
  /** Accept several files (create mode) or exactly one (edit/replace mode). */
  multiple: boolean;
  /** Server-driven size limit from GET /api/documents/upload/capabilities. */
  maxFileSizeMB: number;
  /** Server-driven extension allow-list, lower-case with leading dot. */
  allowedExtensions: readonly string[];
}

export interface UseFileUploadReturn {
  /** Queue rows in insertion order. */
  items: UploadItem[];
  /** Synchronous mirror of `items` for async runners. */
  itemsRef: React.MutableRefObject<UploadItem[]>;
  /** Single-file view: the first valid file, used by the edit/replace path. */
  selectedFile: File | null;
  uploadingFile: boolean;
  uploadProgress: number;
  fileInputRef: React.RefObject<HTMLInputElement | null>;
  /** Validation error of the single selected file (edit mode only). */
  fileError: string | null;
  handleFileSelect: (e: React.ChangeEvent<HTMLInputElement>) => void;
  handleFileBrowse: () => void;
  handleFileRemove: () => void;
  addFiles: (files: readonly File[]) => void;
  removeItem: (id: string) => void;
  updateItem: (id: string, patch: Partial<UploadItem>) => void;
  replaceItems: (updater: (current: UploadItem[]) => UploadItem[]) => void;
  setUploadingFile: React.Dispatch<React.SetStateAction<boolean>>;
  setUploadProgress: React.Dispatch<React.SetStateAction<number>>;
  resetFile: () => void;
  maxFileSizeMB: number;
}

export const useFileUpload = ({
  multiple,
  maxFileSizeMB,
  allowedExtensions,
}: UseFileUploadOptions): UseFileUploadReturn => {
  const { t } = useTranslation();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const itemsRef = useRef<UploadItem[]>([]);
  const [items, setItemsState] = useState<UploadItem[]>([]);
  const [uploadingFile, setUploadingFile] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);

  const allowed = useMemo(
    () => new Set(allowedExtensions.map((ext) => ext.toLowerCase())),
    [allowedExtensions]
  );

  /**
   * Every write goes through here so the ref is updated in the same tick as
   * the state. Functional React updates would be applied later, which is
   * exactly what the async upload runner must not depend on.
   */
  const replaceItems = useCallback((updater: (current: UploadItem[]) => UploadItem[]) => {
    const next = updater(itemsRef.current);
    itemsRef.current = next;
    setItemsState(next);
  }, []);

  const addFiles = useCallback(
    (files: readonly File[]) => {
      if (files.length === 0) {
        return;
      }
      replaceItems((current) => {
        const base = multiple ? current : [];
        const incoming = multiple ? files : files.slice(0, 1);
        const validated = validateFiles(incoming, base, {
          maxBytes: maxFileSizeMB * 1024 * 1024,
          allowed,
          t: (key, options) => t(key, options as Record<string, unknown>) as string,
        });
        return [...base, ...validated];
      });
    },
    [allowed, maxFileSizeMB, multiple, replaceItems, t]
  );

  const removeItem = useCallback(
    (id: string) => {
      replaceItems((current) => current.filter((item) => item.id !== id));
    },
    [replaceItems]
  );

  const updateItem = useCallback(
    (id: string, patch: Partial<UploadItem>) => {
      replaceItems((current) => patchItem(current, id, patch));
    },
    [replaceItems]
  );

  const handleFileSelect = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const files = e.target.files ? Array.from(e.target.files) : [];
      addFiles(files);
      // Clear the input so selecting the same file again fires onChange.
      e.target.value = '';
    },
    [addFiles]
  );

  const handleFileBrowse = useCallback(() => {
    fileInputRef.current?.click();
  }, []);

  const handleFileRemove = useCallback(() => {
    replaceItems(() => []);
    setUploadProgress(0);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  }, [replaceItems]);

  const resetFile = useCallback(() => {
    replaceItems(() => []);
    setUploadingFile(false);
    setUploadProgress(0);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  }, [replaceItems]);

  const selectedFile = useMemo(() => {
    const first = items.find((item) => item.status !== 'error');
    return first ? first.file : null;
  }, [items]);

  const fileError = useMemo(() => {
    if (multiple) {
      return null;
    }
    const failed = items.find((item) => item.status === 'error');
    return failed?.error ?? null;
  }, [items, multiple]);

  return {
    items,
    itemsRef,
    selectedFile,
    uploadingFile,
    uploadProgress,
    fileInputRef,
    fileError,
    handleFileSelect,
    handleFileBrowse,
    handleFileRemove,
    addFiles,
    removeItem,
    updateItem,
    replaceItems,
    setUploadingFile,
    setUploadProgress,
    resetFile,
    maxFileSizeMB,
  };
};
